import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import {
  func, shape, string, arrayOf, oneOf
} from 'prop-types';

import { useDispatch } from 'react-redux';
import { withLang, langType, getTranslation } from '../../../../i18n';
import { surveyTypeValues } from '../../../../constants/types';
import { API_DATE_FORMAT } from '../../../../constants';

import { isSurveyForDegreeProgram } from '../../../../util';
import { getSavedDegreePrograms } from '../../../../api/degreePrograms';

import { MultiSelect } from '../../../Select';
import DatePicker from '../DatePicker';
import LabelWrapper from '../../../LabelWrapper';

import CollapsibleSearch from '../../../Search/CollapsibleSearch';
import styles from './surveySearch.module.css';
import { addErrorMessageAction } from '../../../../actions';

const makeStateOptions = (t) => ([
  { value: 'open', label: t('common.open') },
  { value: 'closed', label: t('common.closed') },
  { value: 'pending', label: t('common.pending') }
]);

const buildCriteria = (criteria) => ({
  title: criteria.title || '',
  degreeProgramCodes: criteria.degreeProgramCodes || [],
  states: criteria.states || [],
  startDateAfter: criteria.startDateAfter || null,
  startDateBefore: criteria.startDateBefore || null
});

const SurveySearch = ({
  onChange, defaultCriteria, type, translate: t, lang
}) => {
  const dispatch = useDispatch();
  const [criteria, setCriteria] = useState(buildCriteria(defaultCriteria));

  useEffect(() => {
    setCriteria(buildCriteria(defaultCriteria));
  }, [defaultCriteria]);

  const [degreePrograms, setDegreePrograms] = useState([]);
  useEffect(() => {
    if (isSurveyForDegreeProgram(type)) {
      getSavedDegreePrograms(type)
        .then((dps) => setDegreePrograms(dps))
        .catch((e) => dispatch(addErrorMessageAction(e)));
    } else {
      setDegreePrograms([]);
    }
  }, [type]);

  const degreeProgramOptions = useMemo(() => (
    degreePrograms
      .map((dp) => ({ value: dp.code, label: getTranslation(dp.name, lang) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [degreePrograms, lang]);

  const stateOptions = useMemo(() => makeStateOptions(t), [t]);

  const update = useCallback((patch) => {
    setCriteria((prev) => {
      const next = { ...prev, ...patch };
      onChange(next);
      return next;
    });
  }, [onChange]);

  return (
    <CollapsibleSearch>
      <div className={styles.surveyCodeSearch}>
        <LabelWrapper label={t('manageSurveys.search.surveyNameLabel')}>
          <input
            type="text"
            className="formItem"
            placeholder={t('manageSurveys.search.titleSearchPlaceholder')}
            value={criteria.title}
            onChange={(e) => update({ title: e.target.value })}
            data-cy="search-survey-by-name-input"
          />
        </LabelWrapper>
      </div>

      {isSurveyForDegreeProgram(type) && (
        <div className={styles.degreeProgramSearch}>
          <LabelWrapper label={t('manageSurveys.search.degreeProgramSearchPlaceholder')}>
            <MultiSelect
              options={degreeProgramOptions}
              value={criteria.degreeProgramCodes}
              placeholderKey="common.select"
              onChange={(vals) => update({ degreeProgramCodes: vals })}
            />
          </LabelWrapper>
        </div>
      )}

      <div className={styles.stateSearch}>
        <LabelWrapper label={t('manageSurveys.search.stateSearchPlaceholder')} dataCy="survey-state-search">
          <MultiSelect
            options={stateOptions}
            value={criteria.states}
            placeholderKey="common.select"
            onChange={(vals) => update({ states: vals })}
          />
        </LabelWrapper>
      </div>

      <div className={styles.dateSearch}>
        <LabelWrapper label={t('manageSurveys.search.surveyStartedAfterPlaceholder')}>
          <DatePicker
            dayStart
            value={criteria.startDateAfter}
            placeholder={t('common.select')}
            onChange={(date) => {
              const v = date ? date.format(API_DATE_FORMAT) : null;
              return update({ startDateAfter: v });
            }}
          />
        </LabelWrapper>
        <LabelWrapper label={t('manageSurveys.search.surveyStartedBeforePlaceholder')}>
          <DatePicker
            dayEnd
            value={criteria.startDateBefore}
            placeholder={t('common.select')}
            onChange={(date) => {
              const v = date ? date.format(API_DATE_FORMAT) : null;
              return update({ startDateBefore: v });
            }}
          />
        </LabelWrapper>
      </div>
    </CollapsibleSearch>
  );
};

SurveySearch.propTypes = {
  onChange: func.isRequired,
  defaultCriteria: shape({
    type: oneOf(Object.values(surveyTypeValues)).isRequired,
    degreeProgramCodes: arrayOf(string),
    states: arrayOf(string),
    startDateBefore: string,
    startDateAfter: string,
    title: string
  }),
  type: oneOf(Object.values(surveyTypeValues)).isRequired,
  ...langType
};

SurveySearch.defaultProps = {
  defaultCriteria: {
    degreeProgramCodes: [],
    states: [],
    startDateAfter: null,
    startDateBefore: null,
    title: '',
    type: surveyTypeValues.DEGREE_PROGRAM_SURVEY
  }
};

export default withLang(SurveySearch);
